/**
 * Auditoria Bancária — primeiro nível: onde o dado bancário vai dar errado.
 *
 * A tela é organizada por PADRÃO de erro, não por lista de gente. Cada padrão
 * traz o que aconteceu, quantas pessoas, um caso real de exemplo e o que fazer.
 * Só quando o analista quer trabalhar aquele padrão é que a lista aparece.
 *
 * Nada aqui é mascarado: quem trabalha a fila precisa comparar os números lado
 * a lado e sair corrigindo no ERP. Tela admin-only, só leitura.
 *
 * Recorte: titulares e responsáveis financeiros — dependente herda o fornecedor
 * do titular, então apareceria repetindo o achado dele.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Landmark, Search, RefreshCw, Download, X, ChevronRight, CheckCircle2,
} from "lucide-react";
import api from "../services/api";
import toast from "react-hot-toast";

const T = {
  bg: "#f5f7fa", card: "#ffffff", ink: "#2c3e50", inkSoft: "#7f8c8d",
  line: "#e8e8e8", teal: "var(--brand, #20a490)", tealSoft: "#e8f6f4",
  risco: "#e74c3c", riscoSoft: "#fdecea", ambar: "#f39c12", ambarSoft: "#fef5e7",
  azul: "#3498db", azulSoft: "#eaf4fc", verde: "#27ae60", verdeSoft: "#e9f7ef",
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";
const fmtN = (v) => Number(v || 0).toLocaleString("pt-BR");
const fmtData = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const COR_FAIXA = { 1: T.risco, 2: T.ambar, 3: T.azul };
const FUNDO_FAIXA = { 1: T.riscoSoft, 2: T.ambarSoft, 3: T.azulSoft };

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: 20, ...style }}>
    {children}
  </div>
);

const TH = { padding: "10px 8px", fontWeight: 600, position: "sticky", top: 0, background: "#fff", textAlign: "left" };
const TD = { padding: "9px 8px", color: T.ink, verticalAlign: "middle" };

/* Um campo bancário: valor aberto. Realçado quando é ele que destoa. */
const Campo = ({ campo, destaque }) => {
  if (!campo?.presente) return <span style={{ color: T.risco, fontSize: 12 }}>em branco</span>;
  return (
    <span style={{ fontFamily: MONO, fontWeight: destaque ? 700 : 400,
      color: destaque ? T.risco : T.ink, background: destaque ? T.riscoSoft : "transparent",
      padding: destaque ? "1px 5px" : 0, borderRadius: 4 }}>
      {campo.exibicao}
    </span>
  );
};

/* banco · agência · conta numa linha só — o formato que o analista lê rápido */
const Conta = ({ bloco }) => {
  if (!bloco?.conta?.presente && !bloco?.agencia?.presente) {
    return <span style={{ color: T.inkSoft, fontSize: 12 }}>sem conta</span>;
  }
  return (
    <span style={{ fontFamily: MONO, fontSize: 12.5 }}>
      {bloco.banco ?? "—"} · {bloco.agencia?.exibicao || "—"} · {bloco.conta?.exibicao || "—"}
    </span>
  );
};

/* ---------- lista de um padrão ---------- */
function ListaDoPadrao({ padrao, onFechar }) {
  const [dados, setDados] = useState(null);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setDados(null);
    api.get("/auditoria-bancaria/fila", { params: { regra: padrao.codigo, pagina, por_pagina: 100 } })
      .then((r) => setDados(r.data))
      .catch(() => toast.error("Não consegui carregar a lista."));
  }, [padrao.codigo, pagina]);

  const baixarCSV = () => {
    if (!dados?.itens?.length) return;
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const linhas = dados.itens.map((i) => [
      i.matricula, i.nome, i.cpf, i.contrato, i.cd_fornecedor,
      i.cadastro.banco, i.cadastro.agencia.exibicao, i.cadastro.conta.exibicao,
      i.financeiro.banco, i.financeiro.agencia.exibicao, i.financeiro.conta.exibicao,
      i.portal.banco, i.portal.agencia.exibicao, i.portal.conta.exibicao,
    ].map(esc).join(","));
    const csv = [
      "matricula,nome,cpf,contrato,fornecedor,banco_cadastro,agencia_cadastro,conta_cadastro," +
      "banco_financeiro,agencia_financeiro,conta_financeiro,banco_portal,agencia_portal,conta_portal",
      ...linhas,
    ].join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${padrao.codigo}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / dados.por_pagina)) : 1;

  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14,
        width: "100%", maxWidth: 1080, maxHeight: "86vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.line}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <div style={{ fontWeight: 700, color: T.ink, fontSize: 16 }}>{padrao.titulo}</div>
            <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 2 }}>
              {fmtN(padrao.casos)} pessoas · <strong>o que fazer:</strong> {padrao.acao}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={baixarCSV} disabled={!dados?.itens?.length}
              style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, padding: "7px 12px",
                cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              <Download size={14} /> CSV
            </button>
            <button onClick={onFechar} style={{ background: "none", border: "none", cursor: "pointer", color: T.inkSoft }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ overflow: "auto", padding: "0 20px 16px" }}>
          {!dados && <div style={{ padding: 24, color: T.inkSoft }}>Carregando…</div>}
          {dados && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ color: T.inkSoft }}>
                <th style={TH}>Pessoa</th>
                <th style={TH}>Cadastro <span style={{ fontWeight: 400 }}>(banco · ag · conta)</span></th>
                <th style={TH}>Financeiro <span style={{ fontWeight: 400 }}>(quem paga)</span></th>
                <th style={TH}>Portal <span style={{ fontWeight: 400 }}>(última solicitação, log)</span></th>
              </tr></thead>
              <tbody>
                {dados.itens.map((i) => {
                  const cadastroErrado = i.consenso.dissidentes.includes("cadastro");
                  return (
                    <tr key={i.matricula} style={{ borderTop: `1px solid ${T.line}` }}>
                      <td style={TD}>
                        <div style={{ fontWeight: 600 }}>{i.nome}</div>
                        <div style={{ fontSize: 11.5, color: T.inkSoft, fontFamily: MONO }}>
                          {i.cpf} · mat {i.matricula} · forn {i.cd_fornecedor ?? "—"}
                        </div>
                      </td>
                      <td style={{ ...TD, background: cadastroErrado ? T.riscoSoft : "transparent" }}>
                        <Conta bloco={i.cadastro} />
                      </td>
                      <td style={TD}><Conta bloco={i.financeiro} /></td>
                      <td style={{ ...TD, color: T.inkSoft }}><Conta bloco={i.portal} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {dados && totalPaginas > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 12, alignItems: "center", padding: 14 }}>
              <button onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina <= 1}
                style={{ border: `1px solid ${T.line}`, background: "#fff", borderRadius: 8, padding: "4px 12px", cursor: "pointer" }}>anterior</button>
              <span style={{ fontSize: 13, color: T.inkSoft }}>página {pagina} de {totalPaginas}</span>
              <button onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={pagina >= totalPaginas}
                style={{ border: `1px solid ${T.line}`, background: "#fff", borderRadius: 8, padding: "4px 12px", cursor: "pointer" }}>próxima</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- card de um padrão ---------- */
function CardPadrao({ padrao, onAbrir }) {
  const cor = COR_FAIXA[padrao.severidade];
  const ex = padrao.exemplo;
  return (
    <Card style={{ padding: 16, borderLeft: `3px solid ${cor}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: T.ink, fontSize: 15 }}>{padrao.titulo}</div>
          <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 3 }}>{padrao.explicacao}</div>
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: cor }}>{fmtN(padrao.casos)}</div>
          <div style={{ fontSize: 11, color: T.inkSoft }}>pessoas</div>
        </div>
      </div>

      {ex && (
        <div style={{ marginTop: 12, padding: "10px 12px", background: T.bg, borderRadius: 8, fontSize: 12.5 }}>
          <span style={{ color: T.inkSoft }}>Por exemplo, </span>
          <strong style={{ color: T.ink }}>{ex.nome}</strong>
          <span style={{ color: T.inkSoft }}> (mat {ex.matricula}):</span>
          <div style={{ marginTop: 5, display: "flex", gap: 18, flexWrap: "wrap" }}>
            <span><span style={{ color: T.inkSoft }}>cadastro</span> <Conta bloco={ex.cadastro} /></span>
            <span><span style={{ color: T.inkSoft }}>financeiro</span> <Conta bloco={ex.financeiro} /></span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12.5, color: T.ink }}>
          <strong style={{ color: cor }}>O que fazer:</strong> {padrao.acao}
        </div>
        <button onClick={() => onAbrir(padrao)}
          style={{ background: "none", border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 12px",
            fontWeight: 600, fontSize: 13, color: T.ink, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
          ver os casos <ChevronRight size={14} />
        </button>
      </div>
    </Card>
  );
}

/* ---------- aba: padrões ---------- */
function Padroes({ dados, onAbrir }) {
  const [mostrarRuido, setMostrarRuido] = useState(false);
  const faixas = [1, 2];
  return (
    <div>
      {faixas.map((nivel) => {
        const lista = dados.regras.filter((r) => r.severidade === nivel);
        if (!lista.length) return null;
        const meta = dados.faixas.find((f) => f.nivel === nivel);
        return (
          <div key={nivel} style={{ marginBottom: 26 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: COR_FAIXA[nivel] }}>{meta?.label}</h3>
              <span style={{ fontSize: 13, color: T.inkSoft }}>
                {meta?.descricao} · {fmtN(meta?.pessoas)} pessoas
              </span>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {lista.map((p) => <CardPadrao key={p.codigo} padrao={p} onAbrir={onAbrir} />)}
            </div>
          </div>
        );
      })}

      {(() => {
        const ruido = dados.regras.filter((r) => r.severidade === 3);
        if (!ruido.length) return null;
        const meta = dados.faixas.find((f) => f.nivel === 3);
        return (
          <div>
            <button onClick={() => setMostrarRuido((v) => !v)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: T.azul }}>{meta?.label}</h3>
              <span style={{ fontSize: 13, color: T.inkSoft }}>
                {meta?.descricao} · {fmtN(meta?.pessoas)} pessoas · {mostrarRuido ? "esconder" : "mostrar"}
              </span>
            </button>
            {mostrarRuido && (
              <div style={{ display: "grid", gap: 12 }}>
                {ruido.map((p) => <CardPadrao key={p.codigo} padrao={p} onAbrir={onAbrir} />)}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ---------- aba: conferir uma pessoa ---------- */
function Pessoa() {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState(null);
  const [ficha, setFicha] = useState(null);

  const buscar = async (e) => {
    e?.preventDefault();
    if (termo.trim().length < 3) return toast.error("Digite ao menos 3 caracteres.");
    try {
      const { data } = await api.get("/auditoria-bancaria/busca", { params: { termo: termo.trim() } });
      setResultados(data);
      if (data.length === 1) abrir(data[0].matricula);
    } catch { toast.error("Não consegui buscar."); }
  };

  const abrir = async (matricula) => {
    try {
      const { data } = await api.get(`/auditoria-bancaria/pessoa/${matricula}`);
      setFicha(data);
      setResultados(null);
    } catch { toast.error("Não consegui abrir a ficha."); }
  };

  return (
    <div>
      <form onSubmit={buscar} style={{ display: "flex", gap: 8, marginBottom: 18, maxWidth: 520 }}>
        <input value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Nome, CPF ou matrícula"
          style={{ flex: 1, padding: "10px 12px", border: `1px solid ${T.line}`, borderRadius: 8, fontSize: 14 }} />
        <button type="submit" style={{ background: T.teal, color: "#fff", border: "none", borderRadius: 8,
          padding: "0 18px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <Search size={15} /> Buscar
        </button>
      </form>

      {resultados?.length === 0 && <Card><span style={{ color: T.inkSoft }}>Ninguém com esse termo.</span></Card>}
      {resultados?.length > 0 && (
        <Card style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {resultados.map((p) => (
                <tr key={p.matricula} onClick={() => abrir(p.matricula)} style={{ borderTop: `1px solid ${T.line}`, cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = T.tealSoft)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <td style={TD}>{p.nome}</td>
                  <td style={{ ...TD, fontFamily: MONO, color: T.inkSoft }}>{p.cpf}</td>
                  <td style={{ ...TD, fontFamily: MONO }}>{p.matricula}</td>
                  <td style={TD}>{p.tipo === "T" ? "Titular" : "Dependente"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {ficha && <Ficha ficha={ficha} onFechar={() => setFicha(null)} />}
    </div>
  );
}

function Ficha({ ficha, onFechar }) {
  const limpo = ficha.achados.length === 0;
  const cor = limpo ? T.verde : (ficha.achados.some((a) => a.severidade === 1) ? T.risco : T.ambar);
  const fundo = limpo ? T.verdeSoft : (ficha.achados.some((a) => a.severidade === 1) ? T.riscoSoft : T.ambarSoft);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{ficha.nome}</div>
          <div style={{ fontSize: 12.5, color: T.inkSoft, fontFamily: MONO }}>
            {ficha.cpf} · matrícula {ficha.matricula} · contrato {ficha.contrato} · fornecedor {ficha.cd_fornecedor ?? "—"}
            {ficha.fornecedores_no_cpf > 1 && (
              <span style={{ color: T.ambar, fontWeight: 700 }}> · {ficha.fornecedores_no_cpf} cadastros neste CPF</span>
            )}
          </div>
        </div>
        <button onClick={onFechar} style={{ background: "none", border: "none", cursor: "pointer", color: T.inkSoft }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ padding: "11px 13px", borderRadius: 8, background: fundo, color: cor,
        fontWeight: 600, fontSize: 13.5, marginBottom: 16, display: "flex", alignItems: "center", gap: 7 }}>
        {limpo && <CheckCircle2 size={16} />} {ficha.resumo}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead><tr style={{ color: T.inkSoft }}>
          <th style={TH}>Local</th><th style={TH}>Banco</th><th style={TH}>Agência</th>
          <th style={TH}>Conta</th><th style={TH}>Atualizado</th>
        </tr></thead>
        <tbody>
          {ficha.locais.map((l) => (
            <tr key={l.codigo} style={{ borderTop: `1px solid ${T.line}`, opacity: l.vivo ? 1 : 0.55,
              background: l.veredito === "diverge" ? T.riscoSoft : "transparent" }}>
              <td style={{ ...TD, fontWeight: 600 }}>
                {l.label}
                <div style={{ fontSize: 11, color: T.inkSoft, fontWeight: 400 }}>{l.papel}</div>
              </td>
              <td style={{ ...TD, fontFamily: MONO, fontWeight: l.difere_em.includes("banco") ? 700 : 400,
                color: l.difere_em.includes("banco") ? T.risco : T.ink }}>{l.banco ?? "—"}</td>
              <td style={TD}><Campo campo={l.agencia} destaque={l.difere_em.includes("agencia")} /></td>
              <td style={TD}><Campo campo={l.conta} destaque={l.difere_em.includes("conta")} /></td>
              <td style={{ ...TD, color: T.inkSoft, fontSize: 12 }}>{l.quando ? fmtData(l.quando) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {ficha.achados.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {ficha.achados.map((a) => (
            <div key={a.codigo} style={{ display: "flex", gap: 10, padding: "8px 0",
              borderTop: `1px solid ${T.line}`, fontSize: 13 }}>
              <span style={{ width: 4, borderRadius: 4, background: COR_FAIXA[a.severidade], flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, color: T.ink }}>{a.titulo}</div>
                <div style={{ color: T.inkSoft, fontSize: 12.5 }}>{a.explicacao} <em>{a.acao}</em></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {ficha.endereco && !ficha.endereco.tem_numero && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: T.ambar }}>
          Endereço do favorecido sem número{ficha.endereco.numero_no_texto ? " (o número está junto do logradouro)" : ""}.
        </div>
      )}
    </Card>
  );
}

/* ---------- página ---------- */
export default function AuditoriaBancaria() {
  const [aba, setAba] = useState("padroes");
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [atualizando, setAtualizando] = useState(false);
  const [padraoAberto, setPadraoAberto] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/auditoria-bancaria/panorama");
      setDados(data);
      setErro(data.disponivel ? null : "O Oracle do ERP não está configurado neste ambiente.");
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não consegui falar com o ERP.");
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const atualizar = async () => {
    setAtualizando(true);
    try {
      await api.post("/auditoria-bancaria/atualizar");
      toast.success("Relendo o ERP. Em alguns segundos os números mudam.");
      setTimeout(() => { carregar(); setAtualizando(false); }, 12000);
    } catch {
      toast.error("Não consegui disparar a releitura.");
      setAtualizando(false);
    }
  };

  const faixa = (n) => dados?.faixas?.find((f) => f.nivel === n)?.pessoas || 0;

  return (
    <div style={{ background: T.bg, minHeight: "100%", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 22, color: T.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <Landmark size={22} color={T.teal} /> Auditoria Bancária
          </h1>
          <p style={{ fontSize: 13.5, color: T.inkSoft, margin: "4px 0 0", maxWidth: 720 }}>
            O dado bancário mora em cinco lugares que não conversam. Estes são os padrões de erro que travam ou desviam o pagamento. Corrija <strong>antes</strong> da remessa.
          </p>
        </div>
        <button onClick={atualizar} disabled={atualizando}
          style={{ background: "#fff", color: T.ink, border: `1px solid ${T.line}`, borderRadius: 8,
            padding: "8px 14px", fontWeight: 600, cursor: atualizando ? "wait" : "pointer",
            display: "flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={15} /> Reler o ERP
        </button>
      </div>

      {erro && (
        <Card style={{ margin: "18px 0", background: T.ambarSoft, border: `1px solid ${T.ambar}` }}>
          <span style={{ color: T.ambar, fontWeight: 600 }}>{erro}</span>
        </Card>
      )}

      {dados?.disponivel && (
        <>
          <div style={{ display: "flex", gap: 22, margin: "18px 0 20px", flexWrap: "wrap", alignItems: "baseline" }}>
            <div>
              <span style={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, color: T.risco }}>{fmtN(faixa(1))}</span>
              <span style={{ fontSize: 13, color: T.inkSoft, marginLeft: 7 }}>para corrigir agora</span>
            </div>
            <div>
              <span style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: T.ambar }}>{fmtN(faixa(2))}</span>
              <span style={{ fontSize: 13, color: T.inkSoft, marginLeft: 7 }}>para conferir</span>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 12, color: T.inkSoft }}>
              {fmtN(dados.total)} titulares e responsáveis financeiros ·
              lido do ERP em {new Date(dados.calculado_em).toLocaleString("pt-BR")}
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: `1px solid ${T.line}` }}>
            {[{ id: "padroes", nome: "Padrões de erro" }, { id: "pessoa", nome: "Conferir uma pessoa" }].map((a) => (
              <button key={a.id} onClick={() => setAba(a.id)}
                style={{ background: "none", border: "none", borderBottom: `2px solid ${aba === a.id ? T.teal : "transparent"}`,
                  color: aba === a.id ? T.teal : T.inkSoft, fontWeight: 600, fontSize: 14,
                  padding: "10px 14px", cursor: "pointer", marginBottom: -1 }}>
                {a.nome}
              </button>
            ))}
          </div>

          {aba === "padroes" && <Padroes dados={dados} onAbrir={setPadraoAberto} />}
          {aba === "pessoa" && <Pessoa />}
        </>
      )}

      {padraoAberto && <ListaDoPadrao padrao={padraoAberto} onFechar={() => setPadraoAberto(null)} />}
    </div>
  );
}
